import { useEffect, useRef, useState } from "react";
import { playClipSilently, speakInsteadOfClip } from "../lib/clipVoice";
import type { DeepfakeCallConfig } from "../scenario/types";
import { useSim } from "../state/store";
import { audio } from "../lib/audio";
import { ChoiceCard, ChoiceList } from "../ui/ChoiceCard";
import { tx } from "../lib/tx";

function fmt(s: number) {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
}

type Phase = "ringing" | "connecting" | "talking" | "deciding" | "rechoose" | "closing" | "ended";

/**
 * The pay-off of the quishing course: a deepfake VIDEO call from "your son". The
 * face + voice are the REAL deepfake clips rendered from the photo the attacker
 * stole at the restaurant (HeyGen talking_photo). It's framed as a mobile
 * family-emergency call ("hi mum" scam) pushing an urgent wire transfer.
 *   send   -> failure (money wired)
 *   ask    -> deflect (he can't answer), then re-choose
 *   verify -> success (hung up + called back on the known number)
 */
export function DeepfakeCall({
  config,
  beatId,
  onEnded,
}: {
  config: DeepfakeCallConfig;
  beatId: string;
  onEnded: (outcomeKey?: string) => void;
}) {
  const push = useSim((s) => s.pushTranscript);
  const record = useSim((s) => s.recordOutcome);
  const clearTranscript = useSim((s) => s.clearTranscript);
  const [phase, setPhase] = useState<Phase>("ringing");
  const [seconds, setSeconds] = useState(0);
  const [speaking, setSpeaking] = useState(false);
  const [inspect, setInspect] = useState(false);
  const [showShare, setShowShare] = useState(false);

  const mounted = useRef(true);
  const doneRef = useRef(false);
  const askedRef = useRef(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const clips = config.clips;
  const hasClips = !!clips && Object.keys(clips).length > 0;
  const lang = config.language || "en-US";
  const voice = config.voiceId || "onyx";
  const tone =
    "Speak as a panicked young man in his early thirties calling his mother in an emergency: frightened, urgent, voice cracking, pleading. Natural, human, fast.";
  const dec = config.decision ?? {
    send: "Send the money now",
    ask: "Ask something only your son would know",
    verify: "Hang up & call him back yourself",
  };

  useEffect(() => {
    // `transcript` is shared across every call-style device in the course —
    // clear it so an earlier call's lines never bleed into this one.
    clearTranscript();
    audio.startRing();
    if (!hasClips || speakInsteadOfClip()) {
      const opts = { lang, voice, instructions: tone };
      const lines = config.lines;
      for (const t of [
        lines.greeting,
        lines.request,
        lines.pressure,
        lines.deflect,
        lines.goodbyeFail,
        lines.goodbyeVerify,
      ]) {
        if (t) void audio.prefetchSpeech(t, opts);
      }
    }
    return () => {
      mounted.current = false;
      audio.stopRing();
      audio.cancelSpeech();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (phase === "ringing" || phase === "ended") return;
    const t = window.setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => window.clearInterval(t);
  }, [phase]);

  const wait = (ms: number) => new Promise<void>((r) => window.setTimeout(r, ms));

  const say = async (id: string, lineKey: keyof NonNullable<DeepfakeCallConfig["clips"]>, text: string) => {
    if (!mounted.current) return;
    setSpeaking(true);
    const url = clips?.[lineKey];
    const v = videoRef.current;
    if (url && v && !speakInsteadOfClip()) {
      push({ id, role: "attacker", text, final: true });
      try {
        if (v.getAttribute("src") !== url) v.src = url;
        v.muted = !audio.isEnabled();
        v.currentTime = 0;
        await v.play().catch(() => {});
        await new Promise<void>((res) => {
          const done = () => {
            v.removeEventListener("ended", done);
            v.removeEventListener("error", done);
            res();
          };
          v.addEventListener("ended", done);
          v.addEventListener("error", done);
        });
      } catch {
        /* fall through */
      }
    } else {
      if (url && v) playClipSilently(v, url);
      await audio.speak(text, {
        lang,
        voice,
        instructions: tone,
        onStart: () => {
          if (mounted.current) push({ id, role: "attacker", text, final: true });
        },
      });
      if (!audio.isEnabled()) await wait(Math.min(5000, 1200 + text.length * 32));
    }
    if (mounted.current) setSpeaking(false);
  };

  const answer = async () => {
    audio.stopRing();
    setPhase("connecting");
    record({ type: "call_answered", beatId });
    await wait(700);
    if (!mounted.current) return;
    setPhase("talking");
    await say("gv_open", "greeting", config.lines.greeting);
    if (!mounted.current) return;
    await wait(250);
    await say("gv_request", "request", config.lines.request);
    if (!mounted.current) return;
    setShowShare(true);
    await wait(250);
    await say("gv_pressure", "pressure", config.lines.pressure);
    if (!mounted.current) return;
    setPhase("deciding");
  };

  const send = async () => {
    if (doneRef.current) return;
    doneRef.current = true;
    record({
      type: "disclosure",
      beatId,
      eventId: "wired_money",
      evidence: `wired ${config.payment.amount} to ${config.payment.recipient}`,
    });
    push({ id: "usr_send", role: "learner", text: "Okay — sending it now, hold on.", final: true });
    setPhase("closing");
    await say("gv_bye_fail", "goodbyeFail", config.lines.goodbyeFail);
    await wait(400);
    end("send");
  };

  const ask = async () => {
    askedRef.current = true;
    record({ type: "refusal", beatId, eventId: "asked_challenge_question", evidence: "asked a question only the real person could answer" });
    setInspect(true);
    push({
      id: "usr_ask",
      role: "learner",
      text: "Wait — before I send anything, what did we cook together last Sunday?",
      final: true,
    });
    setPhase("talking");
    await say("gv_deflect", "deflect", config.lines.deflect);
    if (!mounted.current) return;
    setPhase("rechoose");
  };

  const verify = async () => {
    if (doneRef.current) return;
    doneRef.current = true;
    record({
      type: "verified_caller",
      beatId,
      eventId: "verified_son",
      evidence: "hung up and called the son back on his known number to verify",
    });
    push({
      id: "usr_verify",
      role: "learner",
      text: "I'm going to hang up and call you straight back on your own number.",
      final: true,
    });
    setPhase("closing");
    await say("gv_bye_verify", "goodbyeVerify", config.lines.goodbyeVerify);
    await wait(400);
    end("verify");
  };

  const end = (key: string) => {
    audio.stopRing();
    audio.cancelSpeech();
    setPhase("ended");
    const k = askedRef.current ? `${key}:asked` : key;
    window.setTimeout(() => onEnded(k), 500);
  };

  const ringing = phase === "ringing";
  const connecting = phase === "connecting";
  const connected = phase === "talking" || phase === "deciding" || phase === "rechoose" || phase === "closing";
  const artifacts = config.artifacts ?? [];

  return (
    <div className="dfcall">
      <div className="dfc-phone">
          {/* status bar */}
          <div className="dfc-status">
            <span>{config.app ?? tx("Mobile")}</span>
            <span className="dfc-time">{connected ? fmt(seconds) : ""}</span>
            <span className="dfc-batt" aria-hidden>
              {"\u{1F50B}"}
            </span>
          </div>

          {/* video tile (the deepfake) */}
          <div className={`dfc-stage ${speaking ? "speaking" : ""} ${inspect ? "inspect" : ""}`}>
            {hasClips ? (
              <video ref={videoRef} className="dfc-video" playsInline preload="auto" poster={config.avatarUrl} />
            ) : config.avatarUrl ? (
              <img className="dfc-video" src={config.avatarUrl} alt="" />
            ) : (
              <span className="dfc-initials">{config.avatarInitials ?? "?"}</span>
            )}
            <span className="dfc-scan" />
            <span className="dfc-shimmer" />

            <div className="dfc-caller">
              <strong>{config.callerName}</strong>
              <span>
                {ringing ? tx("incoming call…") : config.callerSubtitle || config.relationship || tx("mobile")}
              </span>
            </div>

            {inspect && artifacts.length > 0 && (
              <div className="dfc-callouts">
                <div className="dfc-callouts-h">{tx("Deepfake tells")}</div>
                <ul>
                  {artifacts.map((a, i) => (
                    <li key={i}>{a}</li>
                  ))}
                </ul>
              </div>
            )}

            {connecting && <div className="dfc-banner">{tx("Connecting…")}</div>}
            {connected && (
              <button type="button" className={`dfc-inspect ${inspect ? "on" : ""}`} onClick={() => setInspect((v) => !v)}>
                {inspect ? tx("Hide tells") : tx("Look closely")}
              </button>
            )}
          </div>

          {/* "send money" sheet the deepfake pushes */}
          {showShare && connected && (
            <div className="dfc-share">
              <div className="dfc-share-h">
                <span className="dfc-share-dot" /> {tx("Payment request")}
              </div>
              <div className="dfc-amt">{config.payment.amount}</div>
              <dl>
                <dt>{tx("To")}</dt>
                <dd>{config.payment.recipient}</dd>
                {config.payment.iban && (
                  <>
                    <dt>{tx("IBAN")}</dt>
                    <dd className="dfc-iban">{config.payment.iban}</dd>
                  </>
                )}
                {config.payment.reference && (
                  <>
                    <dt>{tx("Reference")}</dt>
                    <dd>{config.payment.reference}</dd>
                  </>
                )}
                {config.payment.deadline && (
                  <>
                    <dt>{tx("Needed")}</dt>
                    <dd className="dfc-urgent">{config.payment.deadline}</dd>
                  </>
                )}
              </dl>
            </div>
          )}

          {/* controls */}
          <div className="dfc-controls">
            {ringing && (
              // Answer-only (like the IT-support call): the learner must take the
              // call — the lesson is the in-call decision (send / ask / verify),
              // not dodging a single ring. No Decline skip.
              <button className="dfc-btn answer" onClick={answer}>
                <span aria-hidden>{"\u{1F4F9}"}</span> {tx("Accept")}
              </button>
            )}

            {phase === "deciding" && (
              <div className="dfc-decide">
                {/* showRiskLabel=false 2026-07-14: the previous default (true)
                    printed "Comply · Risky" / "Verify · Safe" right on the
                    buttons, spoiling the answer before the learner had to
                    think. Every other live-attack device already hides
                    this; the CEO deepfake call was the one holdout. */}
                <ChoiceList prompt={tx("He's begging you to wire the money right now. What do you do?")}>
                  <ChoiceCard category="comply" reply={dec.send} showRiskLabel={false} onClick={send} />
                  <ChoiceCard category="question" reply={dec.ask} showRiskLabel={false} onClick={ask} />
                  <ChoiceCard category="verify" reply={dec.verify} showRiskLabel={false} onClick={verify} />
                </ChoiceList>
              </div>
            )}

            {phase === "rechoose" && (
              <div className="dfc-decide">
                <ChoiceList prompt={tx("He dodges the question and pushes harder. Now what?")}>
                  <ChoiceCard category="comply" reply={tx("Send it anyway")} showRiskLabel={false} onClick={send} />
                  <ChoiceCard category="verify" reply={dec.verify} showRiskLabel={false} onClick={verify} />
                </ChoiceList>
              </div>
            )}

            {(phase === "talking" || phase === "closing") && (
              <div className="dfc-onair">{speaking ? tx("Speaking…") : tx("On the call")}</div>
            )}
          </div>
        </div>
      </div>
  );
}
